import { Breadcrumb } from '@e3dc-react/shell/Elements/Header/Breadcrumbs'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Badge, useTheme, Box } from '@mui/material'
import React, { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { DirtyMark } from '../../icons'
import { useInstallationForm } from '../../installation/hooks'
import { checkTrueRecursively } from '../../utils'
import { InstallationPath, installationPaths } from '../constants'
import useSetAdvancedSettingsPageHeaders from '../hooks/use-set-advanced-settings-page-headers'

export const InstallationFormRoutes: React.FC<{ parentBreadcrumb: Breadcrumb }> = ({ parentBreadcrumb }) => {
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const { t } = useCustomTranslation()
  const {
    control,
    formState: { dirtyFields, errors },
  } = useInstallationForm()

  const [storageDevices, chargingStations, heatpump, meters, ioInterfaces] = useWatch({
    control,
    name: ['storageDevices', 'chargingStations', 'heatpump', 'meters', 'ioInterfaces'],
  })

  const areGeneralSettingsDirty = checkTrueRecursively(dirtyFields.emcDevice) || checkTrueRecursively(dirtyFields.generalSettings)
  const areGeneralSettingsHavingError = !!errors.emcDevice || !!errors.generalSettings
  const isStorageSystemDirty = checkTrueRecursively(dirtyFields.storageDevices) || checkTrueRecursively(dirtyFields.storageSystem)
  const isStorageSystemHavingError = !!errors.storageDevices || !!errors.storageSystem
  const isChargingStationDirty = checkTrueRecursively(dirtyFields.chargingStations)
  const isChargingStationHavingError = !!errors.chargingStations
  const isHeatpumpDirty = checkTrueRecursively(dirtyFields.heatpump)
  const isHeatpumpHavingError = !!errors.heatpump
  const areMetersDirty = checkTrueRecursively(dirtyFields.meters)
  const areMetersHavingError = !!errors.meters
  const isIoInterfaceDirty = checkTrueRecursively(dirtyFields.ioInterfaces)
  const isIoInterfaceHavingError = !!errors.ioInterfaces

  const hasStorageDevice = storageDevices.length > 0
  const hasChargingStations = !!(chargingStations.modbusTcp.length || chargingStations.eebus.length)
  const hasHeatpump = heatpump !== null
  const hasMeters = meters.length > 0
  const hasIoInterfaces = Object.values(ioInterfaces).some(io => io !== null)

  const isNewInstallation = !installationId && !storageId

  const tabs = useMemo(() => {
    let basePath: string
    if (isNewInstallation) {
      basePath = `new-installation/`
    } else if (!storageId) {
      basePath = `installation/${installationId}/properties/advanced-settings/`
    } else {
      basePath = `installation/${installationId}/${storageId}/properties/advanced-settings/`
    }

    return [
      {
        label: <TabLabel title={t('installation.configOverview.title')} isDirty={areGeneralSettingsDirty} hasError={areGeneralSettingsHavingError} />,
        href: `${basePath}${installationPaths.General}`,
      },
      {
        label: <TabLabel title={t('installation.types.storage')} isDirty={isStorageSystemDirty} hasError={isStorageSystemHavingError} />,
        href: `${basePath}${installationPaths.Storage}`,
        disabled: !hasStorageDevice,
        disabledTooltipText: t('installation.common.not-available'),
      },
      {
        label: <TabLabel title={t('installation.evseEditor.title')} isDirty={isChargingStationDirty} hasError={isChargingStationHavingError} />,
        href: `${basePath}${installationPaths.Evse}`,
        disabled: !hasChargingStations,
        disabledTooltipText: t('installation.common.not-available'),
      },
      {
        label: <TabLabel title={t('installation.hvacEditor.title')} isDirty={isHeatpumpDirty} hasError={isHeatpumpHavingError} />,
        href: `${basePath}${installationPaths.Hvac}`,
        disabled: !hasHeatpump,
        disabledTooltipText: t('installation.common.not-available'),
      },
      {
        label: <TabLabel title={t('installation.submeterEditor.title')} isDirty={areMetersDirty} hasError={areMetersHavingError} />,
        href: `${basePath}${installationPaths.Meters}`,
        disabled: !hasMeters,
        disabledTooltipText: t('installation.common.not-available'),
      },
      {
        label: <TabLabel title={t('installation.ioInterfaceEditor.title')} isDirty={isIoInterfaceDirty} hasError={isIoInterfaceHavingError} />,
        href: `${basePath}${installationPaths.Io}`,
        disabled: !hasIoInterfaces,
        disabledTooltipText: t('installation.common.not-available'),
      },
    ]
  }, [
    isNewInstallation,
    installationId,
    storageId,
    t,
    hasStorageDevice,
    hasChargingStations,
    hasHeatpump,
    hasMeters,
    hasIoInterfaces,
    areGeneralSettingsDirty,
    areGeneralSettingsHavingError,
    isStorageSystemDirty,
    isStorageSystemHavingError,
    isChargingStationDirty,
    isChargingStationHavingError,
    isHeatpumpDirty,
    isHeatpumpHavingError,
    areMetersDirty,
    areMetersHavingError,
    isIoInterfaceDirty,
    isIoInterfaceHavingError,
  ])

  useSetAdvancedSettingsPageHeaders({
    parentBreadcrumb,
    tabs,
  })

  const isRouteAvailable: Record<InstallationPath, boolean> = {
    [installationPaths.General]: true,
    [installationPaths.Storage]: hasStorageDevice,
    [installationPaths.Evse]: hasChargingStations,
    [installationPaths.Hvac]: hasHeatpump,
    [installationPaths.Meters]: hasMeters,
    [installationPaths.Io]: hasIoInterfaces,
  }
  const location = useLocation()
  const path = (location.pathname.split('/').slice(-1)[0] || location.pathname.split('/').slice(-2)[0]) as InstallationPath

  if (!isRouteAvailable[path]) return <Navigate to={installationPaths.General} replace />

  return <Outlet />
}

const TabLabel: React.FC<{ title: React.ReactNode; isDirty: boolean; hasError: boolean }> = ({ title, isDirty, hasError }) => {
  const theme = useTheme()

  const renderBadgeContent = (): React.ReactNode => {
    if (isDirty && !hasError) {
      return (
        <Box ml={1}>
          <DirtyMark />
        </Box>
      )
    }
    if (hasError) {
      return (
        <Box fontSize={16} ml={1}>
          !
        </Box>
      )
    }
    return null
  }

  return (
    <Badge
      sx={{
        '& .MuiBadge-badge': { fontSize: 9 },
        position: 'relative',
        color: hasError ? theme.palette.error.main : undefined,
        transition: theme => theme.transitions.create('color', { duration: theme.transitions.duration.standard }),
      }}
      badgeContent={renderBadgeContent()}
    >
      {title}
    </Badge>
  )
}
